'use client';

import { useSyncExternalStore } from 'react';
import { fetchAPI } from '@/core/api/client';

/**
 * Ulubione (Integracje → Ulubione): which products the signed-in customer hearted and how many
 * hearts each product on the page has. One store per page so a grid of 24 cards makes one counts
 * request and one "my hearts" request, not 48 — hearts register their product id and the ids are
 * flushed in a single batch.
 */

let mine: Set<number> | null = null;
let mineFor: string | null = null;
const counts = new Map<number, number>();
const listeners = new Set<() => void>();

const emit = () => {
    listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const requested = new Set<number>();
let pending: number[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const BATCH = 100;

async function flushCounts() {
    flushTimer = null;
    const ids = pending;
    pending = [];
    for (let i = 0; i < ids.length; i += BATCH) {
        const chunk = ids.slice(i, i + BATCH);
        try {
            const data = await fetchAPI(`/wishlist/counts?ids=${chunk.join(',')}`);
            chunk.forEach((id) => counts.set(id, Number(data?.[id] ?? 0)));
        } catch {
            chunk.forEach((id) => requested.delete(id)); // the next heart mounted asks again
        }
    }
    emit();
}

/** Ask for this product's heart count (batched with every other heart on the page). */
export function requestCount(productId: number) {
    if (!productId || requested.has(productId)) return;
    requested.add(productId);
    pending.push(productId);
    if (!flushTimer) flushTimer = setTimeout(flushCounts, 30);
}

/** Load the signed-in customer's hearts once per account (userKey = e-mail). */
export function loadMine(userKey: string) {
    if (mineFor === userKey) return;
    mineFor = userKey;
    fetchAPI('/wishlist/me/ids')
        .then((ids: any) => {
            if (mineFor !== userKey) return;
            mine = new Set(Array.isArray(ids) ? ids.map(Number) : []);
            emit();
        })
        .catch(() => {
            if (mineFor === userKey) mineFor = null;
        });
}

/** Signed out — nothing is hearted any more. */
export function resetMine() {
    if (mine === null && mineFor === null) return;
    mine = null;
    mineFor = null;
    emit();
}

/** Heart / un-heart; returns whether the product is hearted now. Optimistic, rolled back on error. */
export async function toggleFavorite(productId: number): Promise<boolean> {
    const was = mine?.has(productId) ?? false;
    const prevCount = counts.get(productId);
    const next = new Set(mine ?? []);
    if (was) next.delete(productId);
    else next.add(productId);
    mine = next;
    if (prevCount !== undefined) counts.set(productId, Math.max(0, prevCount + (was ? -1 : 1)));
    emit();
    try {
        const res: any = await fetchAPI(`/wishlist/${productId}`, { method: was ? 'DELETE' : 'POST' });
        if (typeof res?.count === 'number') counts.set(productId, res.count);
        emit();
        return !was;
    } catch (err) {
        const rollback = new Set(mine ?? []);
        if (was) rollback.add(productId);
        else rollback.delete(productId);
        mine = rollback;
        if (prevCount !== undefined) counts.set(productId, prevCount);
        else counts.delete(productId);
        emit();
        throw err;
    }
}

/** Drop a heart that was removed elsewhere (Moje konto → Ulubione). */
export function forgetFavorite(productId: number) {
    if (!mine?.has(productId)) return;
    const next = new Set(mine);
    next.delete(productId);
    mine = next;
    const n = counts.get(productId);
    if (n !== undefined) counts.set(productId, Math.max(0, n - 1));
    emit();
}

/** The snapshot is a string so it compares by value; the server one (also used while hydrating) is
 *  "not hearted, no count" — a part of the page hydrating after the store has filled must still
 *  match its server HTML, then re-renders with the real state. */
export function useWishlistEntry(productId: number): { favorited: boolean; count: number | undefined } {
    const snapshot = useSyncExternalStore(
        subscribe,
        () => `${mine?.has(productId) ? 1 : 0}|${counts.get(productId) ?? ''}`,
        () => '0|',
    );
    const [favorited, count] = snapshot.split('|');
    return { favorited: favorited === '1', count: count === '' ? undefined : Number(count) };
}
